import * as React from "react"
import { Outlet, useLocation } from "react-router"

import {
  AppSidebar,
  SidebarShell,
  SecondaryPanelProvider,
  SecondaryPanel,
} from "@/components/sidebar"
import { isExamLockPath } from "@/lib/exam-lock-shell"
import {
  assessmentAddQuestionsOwnsSecondaryPanel,
  isSidebarHiddenPath,
} from "@/lib/focus-workflow"
import { shouldSubmissionDetailSecondaryPanelBeOpen } from "@/lib/submission-detail-nav"
import {
  SIDEBAR_STATE_COOKIE_NAME,
  sidebarDefaultOpenFromCookie,
} from "@/lib/sidebar-state-cookie"
import { ThemeProvider } from "@/components/theme-provider"
import { TooltipProvider } from "@/components/ui/tooltip"
import { ProductProviderRoot } from "@/components/product-provider-root"
import {
  ProductRouteSync,
  useProductDashboardHref,
  useProductOrganizationSettingsHref,
} from "@/contexts/product-route-sync"
import { RepairBuiltinProductMirrors } from "@/components/repair-builtin-product-mirrors"
import { ProductSwitchOverlay } from "@/components/product-switch-overlay"
import { ThemeColorSync } from "@/components/theme-color-sync"
import { DashboardViewProvider } from "@/contexts/dashboard-view-context"
import { ChartVariantProvider } from "@/contexts/chart-variant-context"
import { AskLeoProvider } from "@/components/ask-leo-context"
import { LeoAmbienceProvider } from "@/components/leo-ambience-context"
import { LeoApplyMotionProvider } from "@/components/question-quality/leo-apply-motion"
import { KeyMetricsAskLeoBridge } from "@/components/key-metrics-ask-leo-bridge"
import { SystemBannerProvider } from "@/contexts/system-banner-context"
import { SystemBannerSlot } from "@/components/system-banner-slot"
import { UtilityBarSlot } from "@/components/utility-bar-slot"
import { ShellLayoutProvider, useShellLayout } from "@/contexts/shell-layout-context"
import { isFullWidthUtilityBar, showsUtilityBar, hasFlushSidebar } from "@/lib/shell-layout"
import { DemoStateProvider } from "@/contexts/demo-state-context"
import { CommandMenu } from "@/components/command-menu"
import { CommandMenuProvider } from "@/contexts/command-menu-context"
import {
  buildCommandMenuConfig,
  type CommandMenuConfig,
} from "@/lib/command-menu-config"
import { getCommandMenuSearchDataGroups } from "@/lib/command-menu-search-data"
import { ExamRoleProvider, useExamRole } from "@/contexts/exam-role-context"
import { AskLeoDockedSlot, AskLeoFloatingSlot } from "@/components/ask-leo-shell"
import {
  QuestionComposerInspectorDockedSlot,
  QuestionInspectorShellBridgeProvider,
} from "@/components/question-composer/question-composer-inspector-shell"
import {
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useRailLayoutTransition } from "@exxatdesignux/ui/hooks/use-rail-layout-transition"
import { useAskLeoDockedPanelOpen } from "@/hooks/use-ask-leo-docked-panel-open"
import { CompactHeaderSlotProvider } from "@/components/compact-header-slot"
import { PageHeaderScrollActionsBridge } from "@/components/page-header-scroll-actions-bridge"
import { UtilityBarPageChromeProvider } from "@/components/utility-bar-page-chrome"
import { PageHeaderScrollActionsProvider } from "@exxatdesignux/ui/components/shell/page-header-scroll-actions"
import { cn } from "@/lib/utils"
import { WhatsNewProvider } from "@/components/product-home/product-home-parts"

/**
 * Vite-side root layout — replaces both `app/layout.tsx` (Next root) and
 * `app/(app)/layout.tsx` (Next signed-in app shell).
 *
 * Cookie reading: Next reads `cookies()` server-side. In Vite/SPA we read
 * `document.cookie` synchronously on first render — the API is identical
 * (default `true` if absent, otherwise the persisted state). No
 * hydration mismatch because there is no server render to mismatch
 * against.
 *
 * Provider order is preserved verbatim from the Next layout — these
 * contexts are tightly coupled to DS components (KeyMetrics ↔ Ask Leo,
 * SecondaryPanel ↔ Sidebar) and reorder would break runtime behaviour.
 *
 * The `<Outlet />` in the centre is where each route's element renders.
 */

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.split("=")[1]) : undefined
}

function patchOrganizationSettingsHref(
  config: CommandMenuConfig,
  organizationSettingsHref: string
): CommandMenuConfig {
  return {
    ...config,
    groups: config.groups.map((group) => ({
      ...group,
      items: group.items?.map((item) =>
        item.id === "nav-settings-organization"
          ? { ...item, href: organizationSettingsHref }
          : item
      ),
    })),
  }
}

function AppShellLayout({
  sidebarDefaultOpen,
}: {
  sidebarDefaultOpen: boolean
}) {
  const { pathname, search } = useLocation()
  const askLeoDockedOpen = useAskLeoDockedPanelOpen()
  const railTransition = useRailLayoutTransition()
  const { variant: shellLayoutVariant } = useShellLayout()
  const examLock = isExamLockPath(pathname)
  const focusShell = !examLock && isSidebarHiddenPath(pathname)
  const showFocusSecondaryPanel =
    !examLock && assessmentAddQuestionsOwnsSecondaryPanel(pathname, search)
  const showSubmissionDetailSecondaryPanel =
    !examLock && shouldSubmissionDetailSecondaryPanelBeOpen(pathname)
  // Focus compose keeps the same full-width bar placement as hubs so product,
  // search, and scope stay discoverable. Sidebars still stay hidden.
  const fullWidthUtilityBar =
    !examLock && isFullWidthUtilityBar(shellLayoutVariant)
  // A focus workflow hides the sidebars, not the whole app (that is exam lock).
  // The utility bar still mounts — including on `sidebar-classic`, where those
  // actions normally live in the (now hidden) sidebar.
  const mountUtilityBar =
    !examLock && (focusShell || showsUtilityBar(shellLayoutVariant))
  // Compact flush rail — drop `inset` so collapsed width is exactly
  // `--sidebar-width-icon` (matches the utility-bar leading cluster).
  const flushSidebar = !examLock && !focusShell && hasFlushSidebar(shellLayoutVariant)

  return (
    <SidebarShell
      defaultOpen={sidebarDefaultOpen}
      wrapperClassName="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden"
      data-shell-utility-bar-full={fullWidthUtilityBar ? "true" : undefined}
    >
      {!examLock ? <CommandMenu /> : null}
      {fullWidthUtilityBar ? <UtilityBarSlot /> : null}
      {examLock ? (
        <SecondaryPanelProvider>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-sidebar">
            <Outlet />
          </div>
        </SecondaryPanelProvider>
      ) : (
        <div
          className="relative z-40 flex min-h-0 w-full min-w-0 flex-1 items-stretch overflow-visible has-data-[variant=inset]:bg-sidebar"
          data-app-shell-row=""
          data-ask-leo-open={askLeoDockedOpen ? "true" : undefined}
          suppressHydrationWarning
        >
          <SecondaryPanelProvider>
            <QuestionInspectorShellBridgeProvider>
            {!focusShell ? (
              <AppSidebar variant={flushSidebar ? "sidebar" : "inset"} />
            ) : null}
            <div
              data-app-shell-workspace=""
              className={cn(
                "relative z-40 flex min-h-0 min-w-0 flex-1 flex-col overflow-visible",
                flushSidebar ? "pb-0" : "pb-1.5 md:pb-2",
                fullWidthUtilityBar ? "pt-0" : "pt-1.5 md:pt-2"
              )}
            >
              <SystemBannerSlot />
              {mountUtilityBar && !fullWidthUtilityBar ? (
                <UtilityBarSlot />
              ) : null}
              <div
                data-app-shell-workspace-body=""
                className="relative z-40 flex min-h-0 min-w-0 flex-1 items-stretch overflow-visible"
              >
                <ResizablePanelGroup
                  id="shell-workspace"
                  orientation="horizontal"
                  className="min-h-0 min-w-0 flex-1"
                  {...railTransition}
                >
                  {!focusShell || showFocusSecondaryPanel || showSubmissionDetailSecondaryPanel ? (
                    <SecondaryPanel />
                  ) : null}
                  <ResizablePanel
                    id="shell-main"
                    minSize="30%"
                    className="relative z-0 min-h-0 min-w-0"
                  >
                    <div
                      data-app-shell-main=""
                      className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-visible"
                    >
                      <Outlet />
                    </div>
                  </ResizablePanel>
                  <QuestionComposerInspectorDockedSlot />
                  <AskLeoDockedSlot />
                </ResizablePanelGroup>
                <AskLeoFloatingSlot />
              </div>
            </div>
            </QuestionInspectorShellBridgeProvider>
          </SecondaryPanelProvider>
        </div>
      )}
    </SidebarShell>
  )
}

function AppShell({ sidebarDefaultOpen }: { sidebarDefaultOpen: boolean }) {
  const { role } = useExamRole()
  const dashboardHref = useProductDashboardHref()
  const libraryHref = dashboardHref.replace(/\/dashboard$/, "/library")
  const organizationSettingsHref = useProductOrganizationSettingsHref()
  const commandMenuConfig = React.useMemo(
    () =>
      patchOrganizationSettingsHref(
        patchCommandMenuHrefs(
          buildCommandMenuConfig({
            dataGroups: getCommandMenuSearchDataGroups(role),
          }),
          {
            "nav-dashboard": dashboardHref,
            "nav-library": libraryHref,
            "nav-library-all": `${libraryHref}/all`,
          }
        ),
        organizationSettingsHref
      ),
    [dashboardHref, libraryHref, organizationSettingsHref, role]
  )

  return (
    <>
      <ProductRouteSync />
      <RepairBuiltinProductMirrors />
      <ProductSwitchOverlay />
      <ThemeColorSync />
      <TooltipProvider delayDuration={300}>
        <DashboardViewProvider>
          <ChartVariantProvider>
            <AskLeoProvider>
              <LeoAmbienceProvider>
                <LeoApplyMotionProvider>
                  <KeyMetricsAskLeoBridge>
                  <SystemBannerProvider>
                    <ShellLayoutProvider>
                      <DemoStateProvider>
                        <CommandMenuProvider value={commandMenuConfig}>
                          <CompactHeaderSlotProvider>
                            <PageHeaderScrollActionsProvider>
                              <UtilityBarPageChromeProvider>
                                <PageHeaderScrollActionsBridge />
                                <AppShellLayout
                                  sidebarDefaultOpen={sidebarDefaultOpen}
                                />
                              </UtilityBarPageChromeProvider>
                            </PageHeaderScrollActionsProvider>
                          </CompactHeaderSlotProvider>
                        </CommandMenuProvider>
                      </DemoStateProvider>
                    </ShellLayoutProvider>
                  </SystemBannerProvider>
                </KeyMetricsAskLeoBridge>
                </LeoApplyMotionProvider>
              </LeoAmbienceProvider>
            </AskLeoProvider>
          </ChartVariantProvider>
        </DashboardViewProvider>
      </TooltipProvider>
    </>
  )
}

function patchCommandMenuHrefs(
  config: CommandMenuConfig,
  hrefs: Record<string, string>
): CommandMenuConfig {
  return {
    ...config,
    groups: config.groups.map((group) => ({
      ...group,
      items: group.items?.map((item) =>
        item.id in hrefs ? { ...item, href: hrefs[item.id] } : item
      ),
    })),
  }
}

export function App() {
  const sidebarDefaultOpen = sidebarDefaultOpenFromCookie(
    readCookie(SIDEBAR_STATE_COOKIE_NAME)
  )

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem
      disableTransitionOnChange
    >
      <ExamRoleProvider>
        <ProductProviderRoot>
          <WhatsNewProvider>
            <AppShell sidebarDefaultOpen={sidebarDefaultOpen} />
          </WhatsNewProvider>
        </ProductProviderRoot>
      </ExamRoleProvider>
    </ThemeProvider>
  )
}
